import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Flex,
  HStack,
  Text,
  useTheme,
} from '@chakra-ui/react'
import { addDays, addYears, format } from 'date-fns'
import RefillsIndicatorsList from '../../components/RefillsIndicatorsList/RefillsIndicatorsList'
import Table from '../../components/Table/Table'
import { columns } from './temporalData'
import UnderlineHeading from '../../components/UnderlineText/UnderlineHeading'
import { useLabels } from '../../utils/hooks/useLabels/useLabels'
import { REPOSITORY } from '../../api/repository/Repository'
import usePagination from '../../utils/hooks/usePagination'
import RefillDetail from '../../components/RefillDetail/RefillDetail'
import CustomDatePicker from '../../components/CustomDatePicker/CustomDatePicker'
import Selector from '../../components/Selector/Selector'
import { useLoadingContext } from '../../utils/hooks/Context/LoadingContext'
import ConfirmRefill from '../../components/RefillDetail/ConfirmRefill'
import { useNavigate } from 'react-router-dom'
import usePendingRefills from '../../utils/hooks/usePendingRefills/usePendingRefills'
import {
  DeleteRefillContext,
  useDeleteRefillConfirm,
} from '../../utils/hooks/Context/DeleteRefillConfirmContext'
import ConfirmModal from '../BranchAndCostCenter/subViews/ConfirmModal'

const defaultFilters = {
  sEmpresa: '',
  nPagina: '',
  nAsignacionesPagina: '',
  dDesde: '',
  dHasta: '',
  sCentroCosto: '',
  sSucursal: '',
  sTipoRecarga: '',
  sTipoAsignacion: '',
  bProgramacionAsignacion: '',
}
const LIMIT = 30
function Refills() {
  const navigate = useNavigate()

  const { sEmpresa, sCentroCosto } = REPOSITORY.local.company.getCompanyData()
  const { colors } = useTheme()
  const { texts } = useLabels()
  const {
    open,
    handleOpen,
    handleClose: handleCLoseLoading,
  } = useLoadingContext()
  const {
    pendingRefills,
    columns: pendingColumns,
    onChangeCostCenter,
    onChangeFrom,
    onChangeTo,
    applyFilters,
    getData,
  } = usePendingRefills()
  const {
    handleOpenConfirm,
    isOpenConfirm,
    handleCloseConfirm,
    handleOnConfirm,
  } = useDeleteRefillConfirm(getData)
  const { page, handleChangePage } = usePagination()
  const [totalPages, setTotalPages] = useState(0)
  const [refills, setRefills] = useState([])
  const [indicators, setIndicators] = useState()
  const [formatedIndicators, setFormatedIndicators] = useState({})
  const [confirmState, setConfirmState] = useState({
    open: false,
    confirmed: false,
  })
  const [detailState, setDetailState] = useState({
    nAsignacion: '',
    open: false,
  })

  const [filterData, setFilterData] = useState({
    dHasta: '',
    dDesde: '',
    sCentroCosto: '',
  })
  const [contactOptions, setContactOptions] = useState([])
  const [selectedRefill, setSelectedRefill] = useState(null)

  useEffect(() => {
    REPOSITORY.wsEmpresa.employees
      .getContactosEmpresa()
      .then((res) => {
        const contacts = res?.data?.cContactos || res?.data || []
        const options = contacts
          .filter((c) => c.sEmail || c.sEmailContacto)
          .map((c) => {
            const email = c.sEmail || c.sEmailContacto
            const nombre = c.sNombre || c.sNombreContacto || ''
            return {
              label: nombre ? `${nombre} (${email})` : email,
              value: email,
              id: email,
            }
          })
        setContactOptions(options)
      })
      .catch(() => {})
  }, [])

  const handleRefillStatusClick = (original) => {
    setConfirmState((prevState) => ({
      ...prevState,
      open: true,
      dAsignacion: original.dAsignacion,
      nMontoTotal: original.nMontoTotal || original.nMonto,
      CentroCosto: original.CentroCosto,
      nAsignacion: original.nAsignacion,
      nCantidadCuentas:
        Number(original.nCantidadCuentas) || Number(original.nCantEmpleados),
    }))
  }
  const handleChangeFrom = useCallback((date) => {
    setFilterData((prevState) => ({
      ...prevState,
      dDesde: date ? format(date, 'dd-MM-yyyy') : null,
    }))
  }, [])

  const handleChangeTo = useCallback((date) => {
    setFilterData((prevState) => ({
      ...prevState,
      dHasta: date ? format(date, 'dd-MM-yyyy') : null,
    }))
  }, [])

  const handleChangeCostCenter = useCallback((value) => {
    setFilterData((prevState) => ({
      ...prevState,
      sCentroCosto: value,
    }))
  }, [])

  const formatIndicators = useCallback(() => {
    if (indicators && refills.length > 0 && page === 1) {
      setFormatedIndicators({
        disponibleAmount: indicators.SaldoDisponibleRecarga,
        lastRecharge: refills[0]?.dAsignacion || null,
        lastRechargeAmount: refills[0]?.nMontoTotal || null,
      })
    }
  }, [indicators, refills, page])

  const dataTable = useMemo(() => refills, [refills])
  useEffect(() => {
    if (page === 1) {
      formatIndicators()
    }
  }, [page, formatIndicators])
  const handleIndicators = useCallback(async () => {
    const newIndicators = await REPOSITORY.wsEmpresa.dashboard.getCardsData(
      sEmpresa,
    )
    setIndicators(newIndicators.data)
  }, [sEmpresa])

  const handleRefills = useCallback(async () => {
    handleOpen()
    setRefills([])
    await REPOSITORY.wsEmpresa.refills
      .listRefill({
        ...defaultFilters,
        sCentroCosto: filterData.sCentroCosto || null,
        sEmpresa,
        nPagina: page,
        nAsignacionesPagina: LIMIT,
        dHasta: filterData.dHasta || format(new Date(), 'dd-MM-yyyy'),
        dDesde:
          filterData.dDesde || format(addYears(new Date(), -1), 'dd-MM-yyyy'),
      })
      .then((res) => {
        handleCLoseLoading()
        setTotalPages(res.data.nPaginasTotal)
        setRefills(res.data.cDatosRecarga)
        return res
      })
      .catch(() => handleCLoseLoading())
  }, [page, sEmpresa, filterData])

  const onCloseConfirm = useCallback(() => {
    setConfirmState((prevState) => ({ ...prevState, open: false }))
  }, [handleRefills])

  const handleConfirm = () => {
    setConfirmState((prevState) => ({
      ...prevState,
      open: true,
      confirmed: true,
    }))
  }
  const onConfirm = useCallback(() => {
    if (!confirmState.open && confirmState.confirmed) {
      setConfirmState((prevState) => ({
        ...prevState,
        open: false,
        confirmed: false,
      }))
      navigate(0)
    }
  }, [handleRefills, confirmState.open, confirmState.confirmed])

  const handleSearch = useCallback(() => {
    if (page !== 1) {
      handleChangePage(1)
    } else {
      handleRefills()
    }
  }, [handleChangePage, handleRefills])

  const handleClick = useCallback((value) => {
    const refill = refills.find((r) => r.nAsignacion === value)
    setSelectedRefill(refill || null)
    setDetailState((prevState) => ({
      ...prevState,
      nAsignacion: value,
      open: true,
    }))
  }, [refills])

  const handleClose = useCallback(() => {
    setDetailState({ nAsignacion: '', open: false })
  }, [])

  useEffect(() => {
    handleIndicators()
    return () => {
      setIndicators({})
    }
  }, [])

  useEffect(() => {
    if (!open) {
      handleRefills()
    }
  }, [page])
  useEffect(() => {
    onConfirm()
  }, [onConfirm])

  return (
    <>
      <ConfirmModal
        withPass={false}
        text={'¿Está seguro que desea eliminar esta recarga?'}
        isOpen={isOpenConfirm}
        onClose={handleCloseConfirm}
        onConfirm={handleOnConfirm}
      />
      <Flex direction="column" width="100%" mb="64px">
        <ConfirmRefill
          open={confirmState.open}
          onClose={onCloseConfirm}
          onConfirmed={handleConfirm}
          data={confirmState}
        />
        <Box width="100%" justifyContent="flex-start">
          <RefillsIndicatorsList
            iconColor={colors.primary.base}
            disponibleAmount={formatedIndicators?.disponibleAmount}
            lastRecharge={formatedIndicators?.lastRecharge}
            lastRechargeAmount={formatedIndicators?.lastRechargeAmount}
          />
        </Box>
        <Box mt={10}>
          <UnderlineHeading
            lineHeight="26px"
            color={colors.gray[800]}
            text={texts.recharge_history}
            variant="h5"
          />
        </Box>
        <HStack
          alignItems="center"
          h="32px"
          justifyContent="flex-start"
          spacing="16px"
          mt={12}
        >
          <Box border="1px solid #D7D7D7" w="326px">
            <HStack
              alignItems="center"
              justifyContent="flex-start"
              spacing="16px"
            >
              <Box cursor="pointer" w="100%">
                <CustomDatePicker
                  onChange={handleChangeFrom}
                  placeholder="Desde"
                />
              </Box>
              <Text>{'>'}</Text>
              <Box cursor="pointer" w="100%">
                <CustomDatePicker
                  maxDate={addDays(new Date(), 15)}
                  placeholder="Hasta"
                  onChange={handleChangeTo}
                />
              </Box>
            </HStack>
          </Box>
          <Box w="180px">
            <Selector
              placeholder="Centro de costo"
              isClearable
              options={sCentroCosto}
              onChange={handleChangeCostCenter}
            />
          </Box>
          <Box>
            <Button onClick={handleSearch} variant="searchbar">
              Buscar
            </Button>
          </Box>
        </HStack>
        <Box mt={4}>
          <Table
            onEmployeeClick={handleClick}
            columns={columns}
            onRefillStatusClick={handleRefillStatusClick}
            data={dataTable || []}
            withPagination
            paginationProps={{
              totalPages,
              actualPage: page,
              handlePageChange: handleChangePage,
            }}
          />
        </Box>
        <Box mt={10}>
          <UnderlineHeading
            lineHeight="26px"
            color={colors.gray[800]}
            text={'Recargas por autorizar'}
            variant="h5"
          />
        </Box>
        <HStack
          alignItems="center"
          h="32px"
          justifyContent="flex-start"
          spacing="16px"
          mt={12}
        >
          <Box border="1px solid #D7D7D7" w="326px">
            <HStack
              alignItems="center"
              justifyContent="flex-start"
              spacing="16px"
            >
              <Box cursor="pointer" w="100%">
                <CustomDatePicker onChange={onChangeFrom} placeholder="Desde" />
              </Box>
              <Text>{'>'}</Text>
              <Box cursor="pointer" w="100%">
                <CustomDatePicker
                  maxDate={addDays(new Date(), 15)}
                  placeholder="Hasta"
                  onChange={onChangeTo}
                />
              </Box>
            </HStack>
          </Box>
          <Box w="180px">
            <Selector
              placeholder="Centro de costo"
              isClearable
              options={sCentroCosto}
              onChange={onChangeCostCenter}
            />
          </Box>
          <Box>
            <Button onClick={applyFilters} variant="searchbar">
              Buscar
            </Button>
          </Box>
        </HStack>
        <Box mt={4}>
          <DeleteRefillContext.Provider>
            <Table
              onEmployeeClick={handleClick}
              columns={pendingColumns}
              onDeclineRefill={handleOpenConfirm}
              onRefillStatusClick={handleRefillStatusClick}
              data={pendingRefills || []}
              withPagination
              paginationProps={{
                totalPages,
                actualPage: page,
                handlePageChange: handleChangePage,
              }}
            />
          </DeleteRefillContext.Provider>
        </Box>
        <RefillDetail
          nAsignacion={detailState.nAsignacion}
          open={detailState.open}
          onClose={handleClose}
          refill={selectedRefill}
          contactOptions={contactOptions}
        />
      </Flex>
    </>
  )
}

export default Refills
